// klaviyo-back-in-stock.js

// Klaviyo API revision (https://developers.klaviyo.com/en/reference/api_overview).
// Kept as a single constant so both requests stay in lockstep. Note the nested
// `subscriptions` object on the client-subscription profile resource requires
// revision 2025-01-15 or later — older revisions reject it with
// "'subscriptions' is not a valid field for the resource 'profile'".
const KLAVIYO_REVISION = "2026-04-15";

export class KlaviyoBackInStock {
  constructor(formSelector = "form.notify-form") {
    this.form = document.querySelector(formSelector);
    if (!this.form) return; // no form on page, exit early

    this.messageContainer = document.querySelector(".pdp-sold-out-form__form .form-message");
    this.apiKey = this.form.dataset.klaviyoApiKey;
    this.variantId = this.form.dataset.variantId;
    this.newsletterListId = this.form.dataset.klaviyoNewsletterListId;

    // Pull the storefront's translated copy off the form. Fall back to English
    // so the form still works if a data attribute is ever missing.
    this.notifyText = this.form.querySelector("button")?.textContent.trim() || "Notify me";
    this.sendingText = this.form.dataset.formSendingMessage || "Sending...";
    this.successMessage =
      this.form.dataset.formSuccessMessage || "Success! You will be notified when the product is back in stock.";
    this.errorMessage = this.form.dataset.formErrorMessage || "Unable to process your request. Please try again later.";

    if (this.messageContainer && this.apiKey && this.variantId) {
      this.bindEvents();
    }
  }

  bindEvents() {
    this.form.addEventListener("submit", (e) => this.handleSubmit(e));
  }

  async handleSubmit(event) {
    event.preventDefault();

    const emailInput = this.form.querySelector('input[type="email"]');
    const notifyButton = this.form.querySelector("button");
    const consentCheckbox = this.form.querySelector('input[name="newsletter_consent"]');
    const email = emailInput.value;
    // Only join the newsletter when the shopper has explicitly ticked the
    // marketing consent box. Without a tick we only register the back-in-stock
    // subscription and never touch the newsletter list.
    const wantsNewsletter = Boolean(consentCheckbox && consentCheckbox.checked);

    this.setLoadingState(notifyButton, true);

    // Newsletter opt-in is independent of the back-in-stock request: honour the
    // shopper's marketing consent even if the back-in-stock call fails. It logs
    // its own failures and never surfaces to (or blocks) the shopper.
    if (wantsNewsletter) {
      this.subscribeToNewsletter(email).catch(() => {});
    }

    try {
      const response = await fetch(
        `https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=${this.apiKey}`,
        {
          method: "POST",
          headers: {
            revision: KLAVIYO_REVISION,
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify(this.buildPayload(email)),
        },
      );

      if (response.status === 202) {
        this.showMessage(this.successMessage);
        emailInput.value = "";
        if (consentCheckbox) consentCheckbox.checked = false;
      } else {
        // Klaviyo JSON:API errors live under errors[].detail.
        const data = await response.json().catch(() => ({}));
        this.showMessage(data?.errors?.[0]?.detail || this.errorMessage);
      }
    } catch {
      this.showMessage(this.errorMessage);
    } finally {
      this.setLoadingState(notifyButton, false);
    }
  }

  async subscribeToNewsletter(email) {
    if (!this.newsletterListId || !this.apiKey) return;

    const response = await fetch(`https://a.klaviyo.com/client/subscriptions/?company_id=${this.apiKey}`, {
      method: "POST",
      headers: {
        revision: KLAVIYO_REVISION,
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({
        data: {
          type: "subscription",
          attributes: {
            profile: {
              data: {
                type: "profile",
                attributes: {
                  email,
                  subscriptions: { email: { marketing: { consent: "SUBSCRIBED" } } },
                },
              },
            },
          },
          relationships: {
            list: { data: { type: "list", id: this.newsletterListId } },
          },
        },
      }),
    });

    // fetch() only rejects on network errors, so surface HTTP failures here.
    // Non-blocking: the shopper never sees this, but it makes failures diagnosable.
    if (!response.ok) {
      const body = await response.text().catch(() => "");
      console.warn("Klaviyo newsletter subscription failed", response.status, body);
    }
  }

  buildPayload(email) {
    return {
      data: {
        type: "back-in-stock-subscription",
        attributes: {
          channels: ["EMAIL"],
          profile: {
            data: {
              type: "profile",
              attributes: { email },
            },
          },
        },
        relationships: {
          variant: {
            data: {
              type: "catalog-variant",
              id: `$shopify:::$default:::${this.variantId}`,
            },
          },
        },
      },
    };
  }

  setLoadingState(button, isLoading) {
    button.textContent = isLoading ? this.sendingText : this.notifyText;
    button.disabled = isLoading;
  }

  showMessage(message) {
    if (this.messageContainer) {
      this.messageContainer.textContent = message;
    }
  }
}

export const klaviyoBackInStock = new KlaviyoBackInStock();
